import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, errMsg } from "@/lib/api";
import { setFxConfig } from "@/components/ActionFx";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [settings, setSettings] = useState(null);
  const [perms, setPerms] = useState([]);

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/me/permissions");
      setPerms(data.permissions || []);
    } catch {
      setPerms([]);
    }
  }, []);

  const can = useCallback(
    (code) => {
      if (!code) return true;
      if (perms.includes("*")) return true;
      const module = String(code).split(".")[0];
      return perms.includes(code) || perms.includes(`${module}.*`);
    },
    [perms]
  );

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setFxConfig(data?.ui || {});
    } catch (e) {
      setSettings({});
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!localStorage.getItem(TOKEN_KEY)) {
        setUser(false);
      } else {
        try {
          const { data } = await api.get("/auth/me");
          setUser(data);
          loadPermissions();
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          setUser(false);
        }
      }
      loadSettings();
    })();
  }, [loadSettings, loadPermissions]);

  const login = async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      await Promise.all([loadSettings(), loadPermissions()]);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: errMsg(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setPerms([]);
    loadSettings();
  };

  return (
    <AuthContext.Provider value={{ user, settings, setSettings, login, logout, loadSettings, can, perms, loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
